import { cx } from '@7f-ui'
import { LoadingImage } from '@7f-ui/components/loading-image/loading-image'
import type { ProductType } from '@data-access/graphql/schema.types'
import { formatGermanNumber } from '@utils/utils-format'

import { shopTestId } from '@utils/utils-shop-test-ids'
import Link from 'next/link'

import { useWishlistName } from '../hooks/use-wishlist-name'

type WishlistItemProps = {
  id: string
  name: string
  href?: string
  items: {
    image: string
    productType: ProductType
  }[]
  iconEnd?: React.ReactNode
}

const productLabels: Record<string, readonly [string, string]> = {
  experience: ['Erlebnis', 'Erlebnisse'],
  accommodation: ['Unterkunft', 'Unterkünfte'],
  beach_chair: ['Strandkorb', 'Strandkörbe'],
  bike: ['Fahrrad', 'Fahrräder'],
}

export function WishlistItem({ name, items, id, iconEnd, href }: WishlistItemProps) {
  const { getWishlistName } = useWishlistName()

  const getSubline = () => {
    if (items.length === 0) {
      return 'Die Liste ist leer'
    }

    const productTypes: readonly ProductType[] = [
      'experience',
      'accommodation',
      'beach_chair',
      'bike',
    ]
    const counts = productTypes.reduce((acc, type) => {
      const count = items.filter(item => item.productType === type).length
      if (count > 0) {
        acc.set(type, count)
      }
      return acc
    }, new Map<ProductType, number>())
    return Array.from(counts.entries())
      .map(([type, count]) => {
        const [singular, plural] = productLabels[type]
        return `${formatGermanNumber(count)} ${count === 1 ? singular : plural}`
      })
      .join(', ')
  }

  const images = items.map(item => item.image)
  const wishlistName = getWishlistName(name)

  return (
    <div
      className="flex items-center justify-between gap-4"
      data-testid={shopTestId('WishlistItem.root')}
    >
      {href ? (
        <>
          <Link href={href} className="flex items-center gap-4">
            <WishlistItemBody name={wishlistName} id={id} images={images} subline={getSubline()} />
          </Link>
          <div>{iconEnd}</div>
        </>
      ) : (
        <WishlistItemBody name={wishlistName} id={id} images={images} subline={getSubline()} />
      )}
    </div>
  )
}

function WishlistItemBody({
  name,
  id,
  images,
  subline,
}: {
  name: string
  id: string
  images: string[]
  subline: string
}) {
  return (
    <div className="flex items-center gap-4">
      <div>
        <ImageGrid images={images} id={id} />
      </div>
      <div>
        <p
          className="break-words font-body-copy font-semibold"
          data-testid={shopTestId('WishlistItem.name')}
        >
          {name}
        </p>
        <p className="font-body-small" data-testid={shopTestId('WishlistItem.subline')}>
          {subline}
        </p>
      </div>
    </div>
  )
}

const fallbackColors = ['bg-lightgreen', 'bg-lightblue', 'bg-sand ', 'bg-rose']

const ImageGrid = ({ images, id }: { images: string[]; id: string }) => {
  const items = images.slice(0, 3)

  return (
    <div className="relative h-20 w-28">
      {/* Second image - behind and to the left */}
      <div className="absolute bottom-6 left-1 z-0 size-10">
        {items[1] ? (
          <LoadingImage
            src={items[1]}
            alt=""
            width={40}
            height={40}
            className="size-full rounded-xl object-cover"
            sizes="(min-width: 1024px) 640px, (min-width: 1440px) 828px, 384px"
          />
        ) : (
          <div className={cx('size-full rounded-xl', getColorForId(id, 1))} />
        )}
      </div>
      {/* Third image - behind and to the right */}
      <div className="absolute top-7 right-1 z-0 size-10">
        {items[2] ? (
          <LoadingImage
            src={items[2]}
            alt=""
            width={40}
            height={40}
            className="size-full rounded-xl object-cover"
            sizes="(min-width: 1024px) 640px, (min-width: 1440px) 828px, 384px"
          />
        ) : (
          <div className={cx('size-full rounded-xl', getColorForId(id, 2))} />
        )}
      </div>
      {/* Main center image */}
      <div className="absolute top-1/2 left-1/2 z-10 size-20 -translate-x-1/2 -translate-y-1/2">
        {items[0] ? (
          <LoadingImage
            src={items[0]}
            alt=""
            width={80}
            height={80}
            className="size-full rounded-xl object-cover"
            sizes="(min-width: 1024px) 640px, (min-width: 1440px) 828px, 384px"
          />
        ) : (
          <div className={cx('size-full rounded-xl', getColorForId(id, 0))} />
        )}
      </div>
    </div>
  )
}

function stringToSeed(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return hash
}

function getColorForId(id: string, index: number): string {
  const seed = stringToSeed(id + index.toString())
  const colorIndex = Math.abs(seed) % fallbackColors.length
  return fallbackColors[colorIndex]
}
